import { Inject, Injectable, NgZone, Optional } from '@angular/core';
import Rollbar from 'rollbar';
import { Observable, firstValueFrom } from 'rxjs';
import { HttpRequestError } from '../errors/HttpRequestError';
import { LOGGING_OPTIONS, LOG_CONTEXT_FACTORY, NEW_RELIC_CLIENT, ROLLBAR_CLIENT } from '../logging.constants';
import { ILogContext } from '../models/ILogContext';
import { ILogContextFactory } from '../models/ILogContextFactory';
import { ILoggingOptions } from '../models/ILoggingOptions';
import { BrowserAgent } from '../providers/newRelicFactory';


@Injectable()
export class LoggerService {

    constructor(
        @Inject(LOGGING_OPTIONS) private _options: ILoggingOptions,
        @Inject(ROLLBAR_CLIENT) @Optional() private _rollbar: Rollbar,
        @Inject(NEW_RELIC_CLIENT) @Optional() private _newrelic: BrowserAgent,
        @Inject(LOG_CONTEXT_FACTORY) @Optional() private _logContextFactory: ILogContextFactory,
        private _ngZone: NgZone,
    ) { }

    log(message: string | object, ...args: unknown[]): void {
        this.logToConsole('Log', console.log, message, args);
    }

    info(message: string | object, ...args: unknown[]): void {
        this.logToConsole('Info', console.info, message, args);
    }

    dir(message: unknown): void {
        console.dir(message);
    }

    warn(message: string, ...args: unknown[]): void;
    warn(error: Error, ...args: unknown[]): void;
    warn(messageOrError: string | Error, ...args: unknown[]): void {
        if (messageOrError instanceof Error) {
            console.warn(messageOrError, ...args);
        } else {
            this.logToConsole('Warn', console.warn, messageOrError, args);
        }
    }

    error(message: string, ...args: unknown[]): void;
    error(error: Error, ...args: unknown[]): void;
    error(messageOrError: string | Error, ...args: unknown[]): void {
        if (messageOrError instanceof Error) {
            console.error(messageOrError, ...args);
        } else {
            this.logToConsole('Error', console.error, messageOrError, args);
        }
    }

    async recordError(message: string | Error): Promise<void> {
        await this.record('error', message);
    }

    async recordWarning(message: string | Error): Promise<void> {
        await this.record('warning', message);
    }


    private async record(level: Rollbar.Level, message: string | Error): Promise<void> {
        if (!this._options.enableSending) {
            this.warn('To enable sending log data set \'enableSending\' option');
            return Promise.resolve();
        }

        const context = await this.getLogContext();
        await this.recordWithRetry(level, message, context);
    }

    private async recordWithRetry(level: Rollbar.Level, message: string | Error, context?: ILogContext): Promise<void> {
        const ctor = <{ try?: number }>message.constructor;
        ctor.try = ctor.try ?? 1;
        try {
            const rollbarPayload = context
                ? {
                    person: {
                        id: context.userId,
                        username: context.userName,
                        email: context.email,
                    },
                    custom: { tenantCode: context.tenantCode },
                } : null;

            let rollbarResult: Rollbar.LogResult;
            let rollbarUrl: string;
            if (this._rollbar) {
                rollbarResult = this.logToRollbar(level, message, rollbarPayload);
                rollbarUrl = `https://rollbar.com/occurrence/uuid/?uuid=${rollbarResult.uuid}`;
            } else {
                this.warn('To enable sending log data to Rollbar set "rollbarAccessToken" option');
            }

            if (this._newrelic) {
                const newrelicPayload = {
                    'payload.userId': context.userId,
                    'payload.userName': context.userName,
                    'payload.tenantCode': context.tenantCode,
                    'payload.email': context.email,
                    'payload.rollbar': rollbarUrl ?? 'Rollbar not enabled',
                };
                this.logToNewRelic(message, newrelicPayload);
            } else {
                this.warn('To enable sending log data to NewRelic install "NewRelic Agent" onto host machine');
            }

            return new Promise<void>((resolve) => {
                if (!this._rollbar) {
                    this._ngZone.run(() => resolve());
                    return;
                }
                this._rollbar.wait(() => {
                    this.warn(`Rollbar log: ${rollbarUrl}`);
                    this._ngZone.run(() => resolve());
                });
            });
        }
        catch (err) {
            if (ctor.try >= 3) {
                this.error('Can\'t send to Rollbar, details:');
                this.error(err);
                return Promise.resolve();
            } else {
                ctor.try++;
                return await this.recordWithRetry(level, message, context);
            }
        }
    }

    private logToNewRelic(messageOrError: string | Error, attributes?: { [key: string]: string }): void {
        const error = typeof messageOrError === 'string'
            ? new Error(messageOrError)
            : messageOrError;

        const truncate = (str: string) => str.length > 4000
            ? str.substring(0, 4000) + '\n=======\nTRUNCATED'
            : str;


        if (error instanceof HttpRequestError && typeof error.response === 'string') {
            const newRelicHeaderBase64 = new RegExp(/newrelic: (?<base64>.*)/).exec(error.response)?.groups?.base64;
            if (newRelicHeaderBase64) {
                try {
                    const value = atob(newRelicHeaderBase64);
                    const traceId = JSON.parse(value).d.tr;
                    attributes.traceId = traceId;
                }
                catch (err) {
                    this.error(`Unable to extract NewRelic trace ID from header '${newRelicHeaderBase64}'`);
                    this.error(err);
                }
            }
            attributes['payload.response.text'] = truncate(error.response);
            attributes['payload.response.status'] = error.status.toString();
        }

        error.message = truncate(error.message);
        this._newrelic.noticeError(error, attributes);
    }

    private logToRollbar(level: Rollbar.Level, messageOrError: string | Error, payload: Rollbar.Payload, ...args: Rollbar.LogArgument[]): Rollbar.LogResult {

        this._rollbar.configure({ payload });

        const replacerFunc = () => {
            const visited = new WeakSet();
            return (key: string, value: unknown) => {
                if (typeof value === 'object' && value !== null) {
                    if (value instanceof Observable) {
                        return undefined;
                    }
                    if (visited.has(value)) {
                        return undefined;
                    }
                    visited.add(value);
                }
                return value;
            };
        };

        const argsWithNoCircularRefs = JSON.parse(JSON.stringify(args, replacerFunc()));

        let rollbarArgs;
        if (typeof messageOrError === 'string') {
            rollbarArgs = [messageOrError, ...argsWithNoCircularRefs];
        } else {
            rollbarArgs = [messageOrError.message, messageOrError, ...argsWithNoCircularRefs];
        }

        return this._rollbar[level](...rollbarArgs);
    }

    private logToConsole(logPrefix: string, logFunc: (...data: any[]) => void, message?: string | object, args: unknown[] = []): void {
        // eslint-disable-next-line @typescript-eslint/restrict-template-expressions, @typescript-eslint/no-base-to-string
        logFunc(`%c[${logPrefix}]:%c ${message}`,
            'font-size: 14px; font-weight: bold; color: darkorchid;',
            'font-size: 14px', ...args);
    }

    private async getLogContext(): Promise<ILogContext | null> {
        if (!this._logContextFactory) {
            return null;
        }
        // eslint-disable-next-line rxjs-x/finnish
        const rawContext = this._logContextFactory();
        if (rawContext instanceof Promise) {
            return await rawContext;
        }
        if (rawContext instanceof Observable) {
            return await firstValueFrom(rawContext);
        }
        return rawContext;
    }
}
